import ExcelJS from 'exceljs';
import {
  computeChecklistStats, checklistText, parseChecklist, checklistBucket,
  CHECKLIST_LABELS,
  type ChecklistBucket,
} from './checklist';
import {
  expectedBehaviourText, qualityBucket,
  QUALITY_LABELS,
  type QualityBucket,
} from './expected-behaviour';
import type { Issue } from '../types/issue';

// Excel-Export der Checklist- / Acceptance-Criteria-Auswertung.
// Vier Sheets analog zu Expected-Behaviour:
//   Übersicht (KPIs) · Coverage (Team/Type) · Tickets (Detail mit Links) · Legende & Info.

export type ChecklistExportOptions = {
  jiraBaseUrl: string;
  team: string;                 // '' = alle Teams
  scope: 'open' | 'closed' | 'all';
};

const COLORS = {
  headerBg:      'FF1E3A8A',
  headerFg:      'FFFFFFFF',
  subHeaderBg:   'FFE0E7FF',
  subHeaderFg:   'FF1E3A8A',
  metaBg:        'FFF3F4F6',
  metaFg:        'FF374151',
  columnBg:      'FF334155',
  columnFg:      'FFFFFFFF',
  zebraBg:       'FFF8FAFC',
  border:        'FFCBD5E1',
  linkFg:        'FF1D4ED8',
  covHigh:       'FF10B981',
  covMed:        'FFF59E0B',
  covLow:        'FFF97316',
  covVeryLow:    'FFDC2626',
};

const BUCKET_COLORS: Record<ChecklistBucket, string> = {
  empty:       'FF9E9E9E',
  header_only: 'FFC62828',
  no_items:    'FFEF6C00',
  all_open:    'FFF57C00',
  partial:     'FF1976D2',
  all_done:    'FF2E7D32',
};
const EB_BUCKET_COLORS: Record<QualityBucket, string> = {
  empty: 'FF9E9E9E',
  stub:  'FFC62828',
  short: 'FFEF6C00',
  ok:    'FF2E7D32',
  long:  'FF1976D2',
  huge:  'FF7B1FA2',
};

function coverageColor(pct: number): string {
  if (pct >= 80) return COLORS.covHigh;
  if (pct >= 60) return COLORS.covMed;
  if (pct >= 40) return COLORS.covLow;
  return COLORS.covVeryLow;
}

function issueField(row: Record<string, unknown> | undefined, key: string): string {
  if (!row) return '';
  const v = row[key];
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

function bandRow(ws: ExcelJS.Worksheet, rowIdx: number, cols: number, bg: string, fg: string, text: string, size = 11, bold = true, height = 24) {
  ws.mergeCells(rowIdx, 1, rowIdx, cols);
  const c = ws.getCell(rowIdx, 1);
  c.value = text;
  c.font = { name: 'Calibri', size, bold, color: { argb: fg } };
  c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
  c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
  ws.getRow(rowIdx).height = height;
}

function bucketHint(b: ChecklistBucket): string {
  switch (b) {
    case 'empty':       return 'kein Text gepflegt — Handlungsbedarf';
    case 'header_only': return 'nur Abschnitts-Überschrift, keine Items — vergessene Vorlage';
    case 'no_items':    return 'Freitext ohne Checkbox-Items — kein AC-Format';
    case 'all_open':    return 'Items vorhanden, aber keines abgehakt';
    case 'partial':     return 'Fortschritt in Arbeit';
    case 'all_done':    return 'alle Punkte erledigt';
  }
}

export async function exportChecklistXlsx(rows: Issue[], opts: ChecklistExportOptions): Promise<void> {
  const baseUrl = (opts.jiraBaseUrl || 'https://schueco.atlassian.net').replace(/\/+$/, '');
  const browse = (k: string) => `${baseUrl}/browse/${encodeURIComponent(k)}`;
  const stats = computeChecklistStats(rows);
  const coveragePct   = stats.total    > 0 ? (stats.withText     / stats.total)    * 100 : 0;
  const acPct         = stats.total    > 0 ? (stats.withACHeader / stats.total)    * 100 : 0;
  const donePctItems  = (stats.sumItemsDone + stats.sumItemsOpen) > 0
    ? (stats.sumItemsDone / (stats.sumItemsDone + stats.sumItemsOpen)) * 100 : 0;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'SchueCal Dashboard';
  wb.created = new Date();

  const scopeLabel = opts.scope === 'open' ? 'nur offene Tickets'
    : opts.scope === 'closed' ? 'nur geschlossene Tickets' : 'alle Status';
  const teamLabel = opts.team || 'Alle Teams';

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 1: Übersicht
  // ══════════════════════════════════════════════════════════════════════
  const wsOv = wb.addWorksheet('Übersicht');
  wsOv.columns = [{ width: 32 }, { width: 20 }, { width: 20 }, { width: 40 }];
  const NCOL = 4;

  bandRow(wsOv, 1, NCOL, COLORS.headerBg, COLORS.headerFg,
    'SchueCal · Checkliste / Acceptance Criteria — Qualitätsübersicht', 16, true, 32);

  bandRow(wsOv, 2, NCOL, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'Wertet das JIRA-Feld „Custom field (Checklist Text)" aus. Der Text folgt Markdown-Konventionen: „# Überschrift" für Sektionen (u.a. „# Acceptance Criteria") und „* [x]" bzw. „* []" für erledigte bzw. offene Items.',
    10.5, false, 36);

  bandRow(wsOv, 3, NCOL, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Tickets im Scope:  ${stats.total}     ·     Erstellt:  ${new Date().toLocaleString('de-DE')}`,
    10, true, 20);

  wsOv.getRow(4).height = 6;

  const kpiHead = wsOv.getRow(5);
  ['Kennzahl', 'Wert', 'Anteil', 'Hinweis'].forEach((t, i) => {
    const c = kpiHead.getCell(i + 1);
    c.value = t;
    c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
    c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
  });
  kpiHead.height = 22;

  const kpis: Array<[string, string | number, string, string, number?]> = [
    ['Σ Tickets im Scope',                stats.total,        '',                                'Nach Team- und Status-Filter'],
    ['mit Checklist-Text',                stats.withText,     `${coveragePct.toFixed(1)}%`,      'Feld ist gefüllt',            coveragePct],
    ['ohne Checklist-Text',               stats.withoutText,  `${(100 - coveragePct).toFixed(1)}%`, 'Feld leer'],
    ['mit AC-Header („Acceptance Criteria")', stats.withACHeader, `${acPct.toFixed(1)}%`,        'Explizite AC-Sektion erkannt',  acPct],
    ['mit Checkbox-Items',                stats.withItems,    '',                                'Mindestens 1 Item „* [ ]" oder „* [x]"'],
    ['Σ Items erledigt',                  stats.sumItemsDone, '',                                '„[x]" quer über alle Tickets'],
    ['Σ Items offen',                     stats.sumItemsOpen, '',                                '„[]" quer über alle Tickets'],
    ['Erledigungsquote (Items)',          `${donePctItems.toFixed(1)}%`, '',                     'done / (done + offen)', donePctItems],
  ];
  kpis.forEach((k, i) => {
    const r = wsOv.getRow(6 + i);
    r.getCell(1).value = k[0];
    r.getCell(2).value = k[1];
    r.getCell(3).value = k[2];
    r.getCell(4).value = k[3];
    for (let c = 1; c <= 4; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 11, bold: c <= 2 };
      cell.alignment = { vertical: 'middle', horizontal: c === 2 || c === 3 ? 'right' : 'left', indent: 1 };
      if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    if (k[4] != null) {
      const which = k[2] ? 3 : 2;
      r.getCell(which).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(k[4]) } };
      r.getCell(which).font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    }
    r.height = 22;
  });

  // Bucket-Verteilung
  let row = 6 + kpis.length + 2;
  bandRow(wsOv, row, NCOL, COLORS.columnBg, COLORS.columnFg, 'Qualitäts-Buckets', 12, true, 22);
  row++;
  const bucketOrder: ChecklistBucket[] = ['empty', 'header_only', 'no_items', 'all_open', 'partial', 'all_done'];
  bucketOrder.forEach((b, i) => {
    const n = stats.bucketCounts[b];
    const pct = stats.total > 0 ? (n / stats.total) * 100 : 0;
    const r = wsOv.getRow(row + i);
    r.getCell(1).value = CHECKLIST_LABELS[b];
    r.getCell(2).value = n;
    r.getCell(3).value = `${pct.toFixed(1)}%`;
    r.getCell(4).value = bucketHint(b);
    for (let c = 1; c <= 4; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10.5 };
      cell.alignment = { vertical: 'middle', horizontal: c === 2 || c === 3 ? 'right' : 'left', indent: 1 };
      if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BUCKET_COLORS[b] } };
    r.getCell(1).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 20;
  });

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 2: Coverage (Team + Type)
  // ══════════════════════════════════════════════════════════════════════
  const wsCov = wb.addWorksheet('Coverage');
  wsCov.columns = [{ width: 26 }, { width: 10 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }];
  const NCOL_CV = 6;

  bandRow(wsCov, 1, NCOL_CV, COLORS.headerBg, COLORS.headerFg, 'Coverage · gefüllte Checkliste und AC-Header', 14, true, 28);
  bandRow(wsCov, 2, NCOL_CV, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Erstellt:  ${new Date().toLocaleString('de-DE')}`,
    10, true, 20);
  wsCov.getRow(3).height = 6;

  const covHeader = (title: string, atRow: number) => {
    bandRow(wsCov, atRow, NCOL_CV, COLORS.columnBg, COLORS.columnFg, title, 12, true, 22);
    const h = wsCov.getRow(atRow + 1);
    ['Name', 'Σ', 'mit Text', 'Coverage %', 'mit AC', 'AC %'].forEach((t, i) => {
      const c = h.getCell(i + 1);
      c.value = t;
      c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.columnFg } };
      c.alignment = { vertical: 'middle', horizontal: i === 0 ? 'left' : 'right', indent: 1 };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF475569' } };
    });
    h.height = 20;
  };
  const renderRow = (target: number, name: string, total: number, withText: number, withAC: number, zebra: boolean) => {
    const covPct = total > 0 ? (withText / total) * 100 : 0;
    const acPct  = total > 0 ? (withAC   / total) * 100 : 0;
    const r = wsCov.getRow(target);
    r.getCell(1).value = name;
    r.getCell(2).value = total;
    r.getCell(3).value = withText;
    r.getCell(4).value = covPct / 100; r.getCell(4).numFmt = '0.0%';
    r.getCell(5).value = withAC;
    r.getCell(6).value = acPct / 100;  r.getCell(6).numFmt = '0.0%';
    for (let c = 1; c <= 6; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10.5 };
      cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right', indent: 1 };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    r.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(covPct) } };
    r.getCell(4).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(acPct) } };
    r.getCell(6).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 20;
  };

  let rIdx = 4;
  covHeader('Coverage pro Issue-Type', rIdx);
  rIdx += 2;
  stats.perType.forEach((x, i) => { renderRow(rIdx + i, x.type, x.total, x.withText, x.withAC, i % 2 === 1); });
  rIdx += stats.perType.length + 1;
  covHeader('Coverage pro Team', rIdx);
  rIdx += 2;
  stats.perTeam.forEach((x, i) => { renderRow(rIdx + i, x.team, x.total, x.withText, x.withAC, i % 2 === 1); });

  wsCov.views = [{ state: 'frozen', ySplit: 3 }];

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 3: Tickets (Detailliste)
  // ══════════════════════════════════════════════════════════════════════
  const wsT = wb.addWorksheet('Tickets');
  const T_HEAD = [
    { header: 'Key',                  width: 14, align: 'left'   as const },
    { header: 'Team',                 width: 16, align: 'left'   as const },
    { header: 'Type',                 width: 10, align: 'center' as const },
    { header: 'Status',               width: 14, align: 'left'   as const },
    { header: 'Checklist-Status',     width: 24, align: 'left'   as const },
    { header: 'AC-Header?',           width: 12, align: 'center' as const },
    { header: 'Sektionen',            width: 32, align: 'left'   as const },
    { header: 'Items ✓',              width:  9, align: 'right'  as const },
    { header: 'Items ☐',              width:  9, align: 'right'  as const },
    { header: 'Done %',               width: 10, align: 'right'  as const },
    { header: 'Summary',              width: 40, align: 'left'   as const },
    { header: 'Checklist (Text)',     width: 60, align: 'left'   as const },
    // ── Expected Behaviour ─────────────────────────────────────────
    { header: 'EB Länge (Zeichen)',   width: 12, align: 'right'  as const },
    { header: 'EB Qualitäts-Bucket',  width: 22, align: 'left'   as const },
    { header: 'Expected Behaviour (Text)', width: 60, align: 'left' as const },
  ];
  const NCOL_T = T_HEAD.length;

  bandRow(wsT, 1, NCOL_T, COLORS.headerBg, COLORS.headerFg,
    'Tickets · Checkliste + Expected Behaviour — Detail pro Ticket', 14, true, 28);
  bandRow(wsT, 2, NCOL_T, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'Ein Ticket pro Zeile mit Checkliste UND Expected Behaviour im direkten Vergleich. ' +
    'Sortierung: leere und noch nicht bearbeitete Tickets zuerst — dort ist der Handlungsbedarf am größten.',
    10.5, false, 36);
  bandRow(wsT, 3, NCOL_T, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'Checklist-Status: leer · nur Überschrift ohne Items · Freitext ohne Items · alle offen · teilweise · alle erledigt.      ' +
    'AC-Header?: grün „ja", wenn eine Sektion „Acceptance Criteria/Akzeptanzkriterien/Abnahmekriterien/AC" enthält.      ' +
    'EB Qualitäts-Bucket: leer · zu kurz (<20) · kurz (20–60) · gut (60–500) · lang (500–2000) · überlang (>2 KB).',
    10, false, 40);
  bandRow(wsT, 4, NCOL_T, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Σ Tickets:  ${rows.length}`,
    10, true, 20);
  wsT.getRow(5).height = 6;

  T_HEAD.forEach((h, i) => {
    const col = wsT.getColumn(i + 1);
    col.width = h.width;
    const c = wsT.getRow(6).getCell(i + 1);
    c.value = h.header;
    c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.columnFg } };
    c.alignment = { vertical: 'middle', horizontal: h.align, wrapText: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
  });
  wsT.getRow(6).height = 30;

  const bucketOrderMap: Record<ChecklistBucket, number> = {
    empty: 0, header_only: 1, no_items: 2, all_open: 3, partial: 4, all_done: 5,
  };
  const sorted = [...rows].sort((a, b) => {
    const pa = parseChecklist(checklistText(a));
    const pb = parseChecklist(checklistText(b));
    const ba = bucketOrderMap[checklistBucket(pa)];
    const bb = bucketOrderMap[checklistBucket(pb)];
    if (ba !== bb) return ba - bb;
    // Innerhalb Bucket: weniger Items zuerst (dringlicher)
    return (pa.items.length) - (pb.items.length);
  });

  sorted.forEach((raw, i) => {
    const r = wsT.getRow(7 + i);
    const rec  = raw as unknown as Record<string, unknown>;
    const text = checklistText(raw);
    const p    = parseChecklist(text);
    const b    = checklistBucket(p);
    const donePct = (p.itemsDone + p.itemsOpen) > 0
      ? (p.itemsDone / (p.itemsDone + p.itemsOpen)) * 100 : 0;
    const key = issueField(rec, 'Issue key');
    // Expected Behaviour laden
    const eb   = expectedBehaviourText(raw);
    const ebB  = qualityBucket(eb);

    r.getCell(1).value  = key ? { text: key, hyperlink: browse(key), tooltip: 'In JIRA öffnen' } : '';
    r.getCell(2).value  = issueField(rec, 'Team Name');
    r.getCell(3).value  = issueField(rec, 'Issue Type');
    r.getCell(4).value  = issueField(rec, 'Status');
    r.getCell(5).value  = CHECKLIST_LABELS[b];
    r.getCell(6).value  = p.hasACHeader ? 'ja' : '';
    r.getCell(7).value  = p.sections.slice(0, 4).join(' · ');
    r.getCell(8).value  = p.itemsDone;
    r.getCell(9).value  = p.itemsOpen;
    r.getCell(10).value = (p.itemsDone + p.itemsOpen) > 0 ? donePct / 100 : '';
    r.getCell(10).numFmt = '0.0%';
    r.getCell(11).value = issueField(rec, 'Summary');
    r.getCell(12).value = text;
    r.getCell(13).value = eb.length;
    r.getCell(14).value = QUALITY_LABELS[ebB];
    r.getCell(15).value = eb;

    const zebra = i % 2 === 1;
    for (let c = 1; c <= NCOL_T; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10 };
      cell.alignment = {
        vertical: 'top',
        horizontal: T_HEAD[c - 1].align,
        wrapText: c === 7 || c === 11 || c === 12 || c === 15,
      };
      cell.border = { bottom: { style: 'hair', color: { argb: COLORS.border } } };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    r.getCell(1).font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.linkFg }, underline: true };
    // Bucket-Zelle farbig
    r.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BUCKET_COLORS[b] } };
    r.getCell(5).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    // AC-Header ja/nein farbig
    if (p.hasACHeader) {
      r.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.covHigh } };
      r.getCell(6).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    }
    // Done % farbig
    if ((p.itemsDone + p.itemsOpen) > 0) {
      r.getCell(10).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(donePct) } };
      r.getCell(10).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    }
    // EB Bucket farbig
    r.getCell(14).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EB_BUCKET_COLORS[ebB] } };
    r.getCell(14).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 28;
  });

  wsT.views = [{ state: 'frozen', ySplit: 6, xSplit: 1 }];
  wsT.autoFilter = {
    from: { row: 6, column: 1 },
    to:   { row: 6 + sorted.length, column: NCOL_T },
  };

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 4: Legende & Info
  // ══════════════════════════════════════════════════════════════════════
  const info = wb.addWorksheet('Legende & Info');
  info.columns = [{ width: 26 }, { width: 88 }];
  info.mergeCells(1, 1, 1, 2);
  info.getCell(1, 1).value = 'SchueCal · Checkliste / AC — Legende';
  info.getCell(1, 1).font = { name: 'Calibri', size: 16, bold: true, color: { argb: COLORS.headerFg } };
  info.getCell(1, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
  info.getCell(1, 1).alignment = { vertical: 'middle', indent: 1 };
  info.getRow(1).height = 30;

  const legend: Array<[string, string]> = [
    ['Feld / Bucket', 'Bedeutung'],
    ['Checklist Text',       'JIRA-Custom-Feld „Custom field (Checklist Text)". Enthält Markdown mit „# Überschriften" und „* [x] / * []" für Checkbox-Items.'],
    ['AC-Header',            'Es gibt eine Sektion, die „Acceptance Criteria" / „Akzeptanzkriterien" / „Abnahmekriterien" / „AC" heißt (case-insensitive).'],
    ['Bucket „leer"',        'Feld ist gar nicht gepflegt.'],
    ['Bucket „nur Überschrift ohne Items"', 'Überschrift(en) vorhanden, aber keine Checkbox-Items — Vorlage wurde nicht ausgefüllt.'],
    ['Bucket „Freitext ohne Items"', 'Text vorhanden, aber weder Überschrift noch Checkbox-Items — kein strukturiertes AC.'],
    ['Bucket „alle Items offen"',    'Items vorhanden, keines abgehakt — Arbeit noch nicht begonnen bzw. Fortschritt nicht gepflegt.'],
    ['Bucket „teilweise erledigt"',  'Fortschritt in Arbeit — Mix aus [x] und [].'],
    ['Bucket „alle Items erledigt"', 'Alle Items sind abgehakt.'],
    ['',                     ''],
    ['Coverage-Ampel',       '≥ 80 % grün · 60–79 % gelb · 40–59 % orange · < 40 % rot'],
    ['',                     ''],
    ['Kennzahlen',           'Σ Items = summiert über alle Tickets im Scope. „Done %" pro Ticket = erledigte Items / Gesamt-Items dieses Tickets.'],
    ['',                     ''],
    ['Vorgehen',
      '1) Sheet „Tickets" öffnen — leere und „nur Überschrift"-Tickets stehen oben.  ' +
      '2) Nach Team/Type filtern, gezielt an die Verantwortlichen geben.  ' +
      '3) AC-Vorlage konsequent einfordern (Header „# Acceptance Criteria" + Items als „* [ ]"), ' +
      'damit die Coverage-Kennzahlen beim nächsten Export steigen.'],
  ];
  legend.forEach((l, i) => {
    const r = info.getRow(2 + i);
    r.getCell(1).value = l[0];
    r.getCell(2).value = l[1];
    r.getCell(1).font = { name: 'Calibri', size: 11, bold: i === 0 };
    r.getCell(2).font = { name: 'Calibri', size: 10.5 };
    r.getCell(1).alignment = { vertical: 'top' };
    r.getCell(2).alignment = { vertical: 'top', wrapText: true };
    r.height = l[1].length > 100 ? 48 : 20;
    if (i === 0) {
      for (let c = 1; c <= 2; c++) {
        r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
        r.getCell(c).font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
      }
    }
  });

  // ─── Datei erzeugen & Download ──────────────────────────────────────────
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = (opts.team || 'alle-teams').replace(/[^\w-]+/g, '-').toLowerCase();
  const filename = `schuecal-checklist-ac_${slug}_${opts.scope}_${stamp}.xlsx`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
